import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaSearch, FaTimes, FaImage } from 'react-icons/fa'
import { axiosInstance } from '../context/AuthContext'

const SearchModal = ({ open, onClose }) => {
  const [query, setQuery] = useState('')
  const [artworks, setArtworks] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    } else {
      setQuery('')
      setArtworks([])
      setIsLoading(false)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 2) {
      setArtworks([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const handle = setTimeout(async () => {
      try {
        const artworksRes = await axiosInstance.get(`/artworks/search?q=${encodeURIComponent(q)}`)
        setArtworks(artworksRes.data || [])
      } catch (error) {
        console.error('Search failed:', error)
        setArtworks([])
      } finally {
        setIsLoading(false)
      }
    }, 300)

    return () => clearTimeout(handle)
  }, [query, open])

  if (!open) return null

  const goArtwork = (id) => {
    onClose()
    navigate(`/artwork/${id}`)
  }

  const trimmed = query.trim()
  const showEmpty = trimmed.length >= 2 && !isLoading && artworks.length === 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-20 sm:pt-28"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden bg-white rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <FaSearch className="text-gray-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search artwork..."
            className="flex-1 text-base outline-none"
          />
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close search">
            <FaTimes />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {trimmed.length < 2 && (
            <p className="px-4 py-6 text-sm text-center text-gray-400">
              Type at least 2 characters to search.
            </p>
          )}

          {isLoading && (
            <p className="px-4 py-6 text-sm text-center text-gray-400">Searching...</p>
          )}

          {showEmpty && (
            <p className="px-4 py-6 text-sm text-center text-gray-400">
              No results found for “{trimmed}”.
            </p>
          )}

          {artworks.length > 0 && (
            <div className="py-2">
              <p className="px-4 py-1 text-xs font-semibold tracking-wide text-gray-400 uppercase">
                Artwork
              </p>
              {artworks.map((w) => (
                <button
                  key={w._id}
                  onClick={() => goArtwork(w._id)}
                  className="flex items-center w-full gap-3 px-4 py-2 text-left hover:bg-gray-100"
                >
                  {w.imageBase64 ? (
                    <img src={w.imageBase64} alt="" className="object-cover rounded h-9 w-9" />
                  ) : (
                    <span className="flex items-center justify-center text-gray-500 bg-gray-200 rounded h-9 w-9">
                      <FaImage size={14} />
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className="block font-medium text-gray-800 truncate">{w.title}</span>
                    <span className="block text-xs text-gray-500 truncate">{w.artist_email}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchModal
